import { calculateDistance } from './geoUtils';

export interface GeoPoint { latitude?: number | null; longitude?: number | null }
export type CoverageStatus = 'inside' | 'near' | 'outside' | 'unknown';
export interface Coverage {
  distanceKm: number | null;
  radiusKm: number | null;
  overrunKm: number | null;
  inRange: boolean | null;
  status: CoverageStatus;
}
export interface AssignmentPartner extends GeoPoint {
  id: string; name: string; active: boolean; services: readonly string[];
  radius?: number | null; leadCount?: number;
}

export function validCoordinates(point: GeoPoint): point is { latitude: number; longitude: number } {
  return typeof point.latitude === 'number' && Number.isFinite(point.latitude) && Math.abs(point.latitude) <= 90
    && typeof point.longitude === 'number' && Number.isFinite(point.longitude) && Math.abs(point.longitude) <= 180;
}

export function coverageFor(lead: GeoPoint, partner: GeoPoint & { radius?: number | null }): Coverage {
  const radiusKm = typeof partner.radius === 'number' && Number.isFinite(partner.radius) && partner.radius > 0 ? partner.radius : null;
  const distanceKm = validCoordinates(lead) && validCoordinates(partner)
    ? calculateDistance(lead.latitude, lead.longitude, partner.latitude, partner.longitude) : null;
  if (distanceKm === null || radiusKm === null) return { distanceKm, radiusKm, overrunKm: null, inRange: null, status: 'unknown' };
  const inRange = distanceKm <= radiusKm;
  return { distanceKm, radiusKm, overrunKm: Math.max(0, distanceKm - radiusKm), inRange,
    // The 50% visual band follows partners.php. It never increases routing coverage.
    status: inRange ? 'inside' : distanceKm <= radiusKm * 1.5 ? 'near' : 'outside' };
}

// Mirrors api/internal/service-catalog.php. The server still authorizes every assignment.
function serviceTier(services: readonly string[], leadService: string): number | null {
  if (services.includes(leadService)) return 0;
  if ((leadService === 'ανακαινίζωΕνεργειακά' || leadService === 'ανακαινίζωΠλήρηςΑνακαίνιση')
    && ['αντλίεςΘερμότητας', 'κλιματιστικά', 'φωτοβολταϊκά', 'φυσικόΑέριο', 'ηλιακά'].some(service => services.includes(service))) return 1;
  return null;
}

export function rankAssignmentPartners<T extends AssignmentPartner>(lead: GeoPoint & { service: string }, partners: readonly T[]) {
  return partners.flatMap(partner => {
    const tier = serviceTier(partner.services, lead.service);
    return partner.active && tier !== null ? [{ ...partner, tier, coverage: coverageFor(lead, partner) }] : [];
  }).sort((a, b) => {
    if (a.coverage.inRange === true && b.coverage.inRange !== true) return -1;
    if (b.coverage.inRange === true && a.coverage.inRange !== true) return 1;
    if (a.coverage.inRange === true && b.coverage.inRange === true) {
      if (a.tier !== b.tier) return a.tier - b.tier;
      if ((a.leadCount ?? 0) !== (b.leadCount ?? 0)) return (a.leadCount ?? 0) - (b.leadCount ?? 0);
    }
    const distance = (a.coverage.distanceKm ?? Infinity) - (b.coverage.distanceKm ?? Infinity);
    return (Number.isNaN(distance) ? 0 : distance) || a.id.localeCompare(b.id);
  });
}

const number = new Intl.NumberFormat('el-GR', { maximumFractionDigits: 1 });
export const formatKm = (value: number | null): string => value === null ? 'Άγνωστη' : `${value > 0 && value < 0.1 ? '<0,1' : number.format(value)} χλμ`;
export const COVERAGE_LABELS: Record<CoverageStatus, string> = {
  inside: 'Εντός ακτίνας', near: 'Κοντά, εκτός ακτίνας', outside: 'Εκτός ακτίνας', unknown: 'Άγνωστη κάλυψη',
};
